export function parseLocationIdsFromRowFormatText(rowFormatText: string): string[] {
  const text = String(rowFormatText || "").replace(/\r\n/g, "\n");
  const ids: string[] = [];
  const seen = new Set<string>();

  const lines = text.split("\n");

  const normalizeHeaderToken = (s: string) =>
    String(s || "").trim().replace(/[<>]/g, "").toLowerCase();

  // Prefer parsing a camp-data pipe table header so we can extract locationId even when it is not the first column
  // (e.g. finalized_output.txt wrappedCurly format: offeringId ... locationId ...).
  const findHeader = (): { headerLineIndex: number; wrapper: "wrappedCurly" | "pipe"; locationIdIndex: number } | null => {
    for (let i = 0; i < lines.length; i++) {
      const raw = String(lines[i] || "");
      const t = raw.trim();
      if (!t) continue;

      // Accept header either as a standalone pipe/wrappedCurly header line OR as:
      // RowFormat: {{{|...|}}} (single-line header)
      const rowFormatMatch = /^RowFormat\s*:\s*(.+)$/i.exec(t);
      const candidate = rowFormatMatch ? String(rowFormatMatch[1] || "").trim() : t;

      const isWrappedCurly = candidate.startsWith("{{{|") && candidate.endsWith("|}}}");
      const isPipe = candidate.startsWith("|") && candidate.endsWith("|");
      if (!isWrappedCurly && !isPipe) continue;

      const prefix = isWrappedCurly ? "{{{|" : "|";
      const suffix = isWrappedCurly ? "|}}}" : "|";
      const inner = candidate.slice(prefix.length, candidate.length - suffix.length);
      const headerTokens = inner.split("|").map((x) => String(x || "").trim()).filter(Boolean);
      if (headerTokens.length < 2) continue;
      const normalized = headerTokens.map(normalizeHeaderToken);
      const idx = normalized.indexOf("locationid");
      if (idx < 0) continue;
      return { headerLineIndex: i, wrapper: isWrappedCurly ? "wrappedCurly" : "pipe", locationIdIndex: idx };
    }
    return null;
  };

  const header = findHeader();
  if (header) {
    const prefix = header.wrapper === "wrappedCurly" ? "{{{|" : "|";
    const suffix = header.wrapper === "wrappedCurly" ? "|}}}" : "|";
    for (let i = header.headerLineIndex + 1; i < lines.length; i++) {
      const t = String(lines[i] || "").trim();
      if (!t) continue;
      if (/^rowformat\s*:/i.test(t)) continue;
      if (!(t.startsWith(prefix) && t.endsWith(suffix))) continue;
      const inner = t.slice(prefix.length, t.length - suffix.length);
      const cols = inner.split("|"); // keep empties for column alignment
      const locId = String(cols[header.locationIdIndex] || "").trim();
      if (!locId) continue;
      if (seen.has(locId)) continue;
      seen.add(locId);
      ids.push(locId);
    }
    return ids;
  }

  // Fallback: treat first column as locationId (legacy missingRowFormatText shape).
  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    if (t.toLowerCase().startsWith("rowformat:")) continue;
    if (!t.startsWith("|")) continue;
    const parts = t.split("|").filter((p) => p.length > 0);
    if (parts.length === 0) continue;
    const locId = String(parts[0] || "").trim();
    if (!locId) continue;
    if (seen.has(locId)) continue;
    seen.add(locId);
    ids.push(locId);
  }

  return ids;
}

export function normalizePrefillToCampDataWrappedCurlyTable(prefillText: string): string {
  const text = String(prefillText || "").replace(/\r\n/g, "\n");
  const trimmed = text.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("{{{|")) return trimmed;

  const lines = text.split("\n");
  const firstNonEmptyIndex = lines.findIndex((l) => String(l || "").trim().length > 0);
  if (firstNonEmptyIndex < 0) return "";

  const first = String(lines[firstNonEmptyIndex] || "").trim();

  const wrapPipeLine = (line: string): string | null => {
    const t = String(line || "").trim();
    if (!t.startsWith("|") || !t.endsWith("|")) return null;
    const parts = t.split("|");
    // Remove the leading/trailing empty cells caused by the outer pipes.
    const inner = parts.slice(1, -1).map((x) => String(x ?? ""));
    return `{{{|${inner.join("|")}|}}}`;
  };

  // Case A: input starts with RowFormat: |<...>|...| then rows as |...| lines
  const m = /^RowFormat\s*:\s*(.+)$/i.exec(first);
  if (m) {
    const spec = String(m[1] || "").trim();
    const specLine = spec.startsWith("|") && spec.endsWith("|") ? spec : `|${spec.replace(/^\|+|\|+$/g, "")}|`;
    const header = wrapPipeLine(specLine);
    if (!header) return trimmed;

    const out: string[] = [header];
    for (let i = firstNonEmptyIndex + 1; i < lines.length; i++) {
      const wrapped = wrapPipeLine(lines[i]);
      if (!wrapped) continue;
      out.push(wrapped);
    }
    return out.join("\n").trim();
  }

  // Case B: plain pipe table header/rows (no RowFormat line) — wrap each pipe row
  if (first.startsWith("|") && first.endsWith("|")) {
    const out: string[] = [];
    for (const line of lines) {
      const wrapped = wrapPipeLine(line);
      if (!wrapped) continue;
      out.push(wrapped);
    }
    return out.join("\n").trim() || trimmed;
  }

  return trimmed;
}

export function ensureRowFormatPrefillWithWrappedCurlyHeader(prefillText: string): string {
  const text = String(prefillText || "").replace(/\r\n/g, "\n");
  const trimmed = text.trim();
  if (!trimmed) return "RowFormat: ";

  if (/^rowformat\s*:/i.test(trimmed)) {
    const m = /^RowFormat\s*:\s*(.+)$/i.exec(trimmed.split("\n")[0] || "");
    const head = String(m?.[1] || "").trim();
    if (head.startsWith("{{{|") && head.endsWith("|}}}")) return trimmed;
    // Normalize then re-wrap with RowFormat header.
  }

  const table = normalizePrefillToCampDataWrappedCurlyTable(trimmed);
  const lines = table.split("\n").map((l) => String(l || "")).filter((l) => l.length > 0);
  if (lines.length === 0) return "RowFormat: ";
  const header = lines[0].trim();
  const rows = lines.slice(1);
  return rows.length > 0 ? `RowFormat: ${header}\n${rows.join("\n")}` : `RowFormat: ${header}`;
}

